// WHAT: Log level type definitions and configuration utilities
// WHY:  Provides type-safe log levels with priority ordering and visual configuration for UI display
// DOMAIN: Logging → Entities → Log Level
// DEPENDENCIES: None (base entity)
// RISKS:
//   - NOTE: Log level priority comparison assumes consistent priority values
//   - NOTE: Icon and color configuration is hardcoded - may not match design system
//   - WARN: getLogLevelConfig may return undefined for invalid levels - callers should handle this

export type LogLevel = "debug" | "error" | "info" | "warn";

export const LOG_LEVELS: LogLevel[] = ["debug", "info", "warn", "error"];

export const LOG_LEVEL_CONFIG = {
  debug: {
    color: "#6b7280", // gray-500
    icon: "🐛",
    label: "Debug",
    priority: 0,
  },
  error: {
    color: "#ef4444", // red-500
    icon: "❌",
    label: "Error",
    priority: 3,
  },
  info: {
    color: "#3b82f6", // blue-500
    icon: "ℹ️",
    label: "Info",
    priority: 1,
  },
  warn: {
    color: "#f59e0b", // amber-500
    icon: "⚠️",
    label: "Warn",
    priority: 2,
  },
} as const;

export const getLogLevelConfig = (level: LogLevel) => {
  const config = LOG_LEVEL_CONFIG[level];
  // NOTE: Unknown level – fall back to debug configuration instead of logging to console
  // TypeScript ensures level is always valid, so config is never undefined
  return config;
};

export const isLogLevelEnabled = (currentLevel: LogLevel, targetLevel: LogLevel): boolean => {
  return LOG_LEVEL_CONFIG[targetLevel].priority >= LOG_LEVEL_CONFIG[currentLevel].priority;
};
